import type { ReactNode } from 'react'

import {
  DangerButton,
  Divider,
  Icon,
  PendingButton,
  PositiveButton,
  Space,
  StatusBadge,
  SubText,
  Table,
  Text,
} from '@procurify/ui'
import { Link } from 'react-router-dom'

import { FormattedShortDate } from '@webapp/core/components/FormattedDate/FormattedShortDate'
import { Price } from '@webapp/core/components/Price'
import { StatusCardLegacy } from '@webapp/core/components/StatusCardLegacy'
import { UserLabel } from '@webapp/core/components/UserLabel'
import { useAppRoutes, useProcurifyIntl } from '@webapp/hooks'
import messages from './messages'
import { TravelTransportTypes } from '../../entities/enums/TravelTransportTypeEnum'
import { type TravelDestination } from '../../entities/TravelDestination'
import { type TravelRequestSummary } from '../../entities/TravelRequestSummary'

export interface ITravelCardProps {
  travel: TravelRequestSummary
  isJustCreated?: boolean
  isJustUpdated?: boolean
  /** Approvals pending tab: row checkbox rendered by the list View. */
  checkboxSlot?: ReactNode
  /** Approvals pending tab: per-row next-approver select (BHV-037). */
  nextApproverSlot?: ReactNode
  /** Status list: per-card overflow menu (send-travel-reminder trigger). */
  actionsSlot?: ReactNode
}

const TRANSPORT_MESSAGES = {
  [TravelTransportTypes.DRIVE]: messages.transportDriveLabel,
  [TravelTransportTypes.FLIGHT]: messages.transportFlightLabel,
  [TravelTransportTypes.RAIL]: messages.transportRailLabel,
  [TravelTransportTypes.SAIL]: messages.transportSailLabel,
} as const

export const TravelCard = ({
  travel,
  isJustCreated = false,
  isJustUpdated = false,
  checkboxSlot,
  nextApproverSlot,
  actionsSlot,
}: ITravelCardProps) => {
  const intl = useProcurifyIntl()
  const { url } = useAppRoutes()

  const StatusButton = travel.isPending
    ? PendingButton
    : travel.isApproved
      ? PositiveButton
      : DangerButton

  const renderTransport = (destination: TravelDestination) => (
    <Space direction='horizontal' align='center' size='small'>
      {/* Only the flight glyph exists in the DS icon font — other transport
          modes render label-only (accepted gap, travels 2.1). */}
      {destination.transport === TravelTransportTypes.FLIGHT && (
        <Icon icon='flight' size={16} />
      )}
      <Text autoMargin={false}>
        {intl.formatMessage(TRANSPORT_MESSAGES[destination.transport])}
      </Text>
    </Space>
  )

  const dataLeft = [
    {
      label: intl.formatMessage(messages.submittedDateLabel),
      render: (
        <Space direction='horizontal' align='center' size='small'>
          <Text bold autoMargin={false}>
            <FormattedShortDate date={travel.dateCreated} />
          </Text>
          {isJustCreated && (
            <StatusBadge
              type='inactive'
              text={intl.formatMessage(messages.createdIndicator)}
            />
          )}
          {isJustUpdated && (
            <StatusBadge
              type='inProgress'
              text={intl.formatMessage(messages.updatedIndicator)}
            />
          )}
        </Space>
      ),
    },
    {
      label: intl.formatMessage(messages.lastModifiedDateLabel),
      render: (
        <Text bold autoMargin={false}>
          <FormattedShortDate date={travel.dateLastModified} />
        </Text>
      ),
    },
    {
      label: intl.formatMessage(messages.requestedByLabel),
      render: <UserLabel user={{ fullName: travel.requesterFullName }} />,
    },
    {
      label: intl.formatMessage(messages.departmentLabel),
      value: travel.categoryLabel,
    },
  ]

  const dataRight: { label: ReactNode; render: ReactNode }[] = []

  if (nextApproverSlot) {
    dataRight.push({
      label: intl.formatMessage(messages.nextApproverLabel),
      render: nextApproverSlot,
    })
  } else if (travel.isPending && travel.nextApproverFullName) {
    dataRight.push({
      label: intl.formatMessage(messages.nextApproverLabel),
      render: <UserLabel user={{ fullName: travel.nextApproverFullName }} />,
    })
  }

  // BHV-027: approver/denier/purchaser derived from log action codes
  if (travel.isApproved && travel.finalApprover) {
    dataRight.push({
      label: intl.formatMessage(messages.finalApprovedByLabel),
      render: (
        <UserLabel user={{ fullName: travel.finalApprover.userFullName }} />
      ),
    })
  }

  if (travel.isDenied && travel.deniedBy) {
    dataRight.push({
      label: intl.formatMessage(messages.deniedByLabel),
      render: <UserLabel user={{ fullName: travel.deniedBy.userFullName }} />,
    })
  }

  if (travel.purchasedBy) {
    dataRight.push({
      label: intl.formatMessage(messages.purchasedByLabel),
      render: (
        <UserLabel user={{ fullName: travel.purchasedBy.userFullName }} />
      ),
    })
  }

  return (
    <StatusCardLegacy>
      {checkboxSlot && (
        <StatusCardLegacy.Left>{checkboxSlot}</StatusCardLegacy.Left>
      )}

      <StatusCardLegacy.Content>
        <StatusCardLegacy.Content.Left>
          <Space>
            <Link to={url('TravelDetailsPage', { uuid: travel.uuid })}>
              <StatusButton icon='flight'>{travel.id}</StatusButton>
            </Link>

            <SubText bold>
              {intl.formatMessage(messages.destinationsLabel, {
                count: travel.destinationCount,
              })}
            </SubText>

            {travel.destinationCount > 0 ? (
              <StatusCardLegacy.Collapsible.Toggle
                label={intl.formatMessage(messages.detailsToggleLabel)}
              />
            ) : null}
          </Space>
        </StatusCardLegacy.Content.Left>

        <StatusCardLegacy.Content.Right>
          <StatusCardLegacy.Content.Table
            headerLeft={{
              render: (
                <Space direction='horizontal'>
                  {travel.isApprovalDelegated && (
                    <StatusBadge
                      type='warning'
                      text={intl.formatMessage(
                        messages.approvalDelegatedIndicator
                      )}
                    />
                  )}
                </Space>
              ),
            }}
            headerRight={{
              render: (
                <Price
                  price={travel.travelBudget}
                  currency={travel.currency.name}
                  fontSize={20}
                />
              ),
              justifyContent: 'flex-end',
            }}
            dataLeft={dataLeft}
            dataRight={dataRight}
          />
        </StatusCardLegacy.Content.Right>
      </StatusCardLegacy.Content>

      {actionsSlot && (
        <StatusCardLegacy.Right>{actionsSlot}</StatusCardLegacy.Right>
      )}

      <StatusCardLegacy.Collapsible>
        <Divider />
        <Table
          aria-label={intl.formatMessage(messages.tripTableAriaLabel, {
            travelId: travel.id,
          })}
        >
          <Table.Head>
            <Table.Row>
              <Table.Cell />
              <Table.Cell>
                {intl.formatMessage(messages.tripTableTransportationHeader)}
              </Table.Cell>
              <Table.Cell>
                {intl.formatMessage(messages.tripTableDepartureHeader)}
              </Table.Cell>
              <Table.Cell>
                {intl.formatMessage(messages.tripTableDepartureDateHeader)}
              </Table.Cell>
              <Table.Cell>
                {intl.formatMessage(messages.tripTableArrivalHeader)}
              </Table.Cell>
              <Table.Cell>
                {intl.formatMessage(messages.tripTableArrivalDateHeader)}
              </Table.Cell>
            </Table.Row>
          </Table.Head>
          <Table.Body>
            {travel.destinations.map((destination) => (
              <Table.Row key={destination.id}>
                <Table.Cell>{destination.sequence}</Table.Cell>
                <Table.Cell>{renderTransport(destination)}</Table.Cell>
                <Table.Cell>
                  {destination.departureAddress.displayLabel}
                </Table.Cell>
                <Table.Cell>
                  <FormattedShortDate date={destination.departureDate} />
                </Table.Cell>
                <Table.Cell>
                  {destination.arrivalAddress.displayLabel}
                </Table.Cell>
                <Table.Cell>
                  <FormattedShortDate date={destination.arrivalDate} />
                </Table.Cell>
              </Table.Row>
            ))}
          </Table.Body>
        </Table>
      </StatusCardLegacy.Collapsible>
    </StatusCardLegacy>
  )
}

TravelCard.displayName = 'TravelCard'
